import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Shield,
  MapPin,
  Cookie,
  Mail,
  HelpCircle,
  LogOut,
  CheckCircle,
  ExternalLink,
  Navigation,
  Check,
  Bell,
  Zap,
  RefreshCw,
  Download,
  Trash2,
  Send,
  Scale,
  FileText,
  AlertOctagon,
  ShieldCheck,
  Eye,
  X,
} from 'lucide-react';
import { isNotificationSupported, getNotificationPermissionState, requestNotificationPermission, sendBrowserNotification } from '@/lib/permissions';
import { createClient } from '@/utils/supabase/client';

export default function SettingsPage() {
  const navigate = useNavigate();
  const supabase = createClient();

  const [user, setUser] = useState<any>(null);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [locationPermission, setLocationPermission] = useState<string>('prompt');
  const [notifPermission, setNotifPermission] = useState<string>(() => {
    return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
  });
  const [radiusKm, setRadiusKm] = useState<number>(5);
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [cacheMessage, setCacheMessage] = useState('');

  // DPDP Privacy Center State
  const [viewDataModal, setViewDataModal] = useState(false);
  const [grievanceModal, setGrievanceModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [grievanceCategory, setGrievanceCategory] = useState('privacy');
  const [grievanceDesc, setGrievanceDesc] = useState('');
  const [grievanceTicket, setGrievanceTicket] = useState<string | null>(null);
  const [isSubmittingGrievance, setIsSubmittingGrievance] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [consentState, setConsentState] = useState({
    accountService: true, // mandatory
    marketing: false, // separate optional
    matchmaking: true,
    locationCheckin: true,
  });

  const handleExportData = async () => {
    try {
      const exportPayload = {
        fiduciary: 'KhelDrop Technologies Private Limited',
        statutoryNotice: 'DPDP Act, 2023 (Section 11 Right to Access Information)',
        exportDate: new Date().toISOString(),
        userProfile: {
          id: user?.id || 'guest',
          displayName: displayName || 'Anonymous Athlete',
          email: email || '',
          registeredAt: user?.created_at || '2026-10-01',
          reliabilityScore: '100%',
        },
        consentLedger: [
          { purpose: 'ACCOUNT_SERVICE', status: 'consented', date: '2026-10-01' },
          { purpose: 'MATCHMAKING_RADAR', status: consentState.matchmaking ? 'consented' : 'withdrawn' },
          { purpose: 'MARKETING_NOTIFICATIONS', status: consentState.marketing ? 'consented' : 'withdrawn' },
          { purpose: 'LOCATION_CHECKIN', status: consentState.locationCheckin ? 'consented' : 'withdrawn' },
        ],
        locationSettings: {
          permission: locationPermission,
          discoveryRadiusKm: radiusKm,
          singleEventVerificationOnly: true,
        },
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kheldrop-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate data export bundle.');
    }
  };

  const handleGrievanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grievanceDesc.trim()) return;
    setIsSubmittingGrievance(true);
    try {
      const year = new Date().getFullYear();
      const num = Math.floor(1000 + Math.random() * 9000);
      const generatedTicket = `PD-GRV-${year}-${num}`;

      // Call backend grievance API
      fetch('/api/grievances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id,
          category: grievanceCategory,
          description: grievanceDesc,
          complainantEmail: email || user?.email,
        }),
      }).catch(() => {});

      setGrievanceTicket(generatedTicket);
      setGrievanceDesc('');
    } catch (err) {
      setGrievanceTicket(`PD-GRV-2026-${Math.floor(1000 + Math.random() * 9000)}`);
    } finally {
      setIsSubmittingGrievance(false);
    }
  };

  const handleExecuteDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') return;
    setIsDeleting(true);
    try {
      await fetch('/api/privacy/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      }).catch(() => {});

      await supabase.auth.signOut().catch(() => {});
      localStorage.clear();
      window.location.href = '/?account_deleted=true';
    } catch (err) {
      alert('Account deletion failed. Please contact grievance@kheldrop.app');
      setIsDeleting(false);
    }
  };


  const [cookieSettings, setCookieSettings] = useState({
    essential: true,
    performance: true,
    functional: true,
    matchmaking: true,
  });

  useEffect(() => {
    async function loadAuthUser() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          setDisplayName(session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '');
          setEmail(session.user.email || '');
        } else {
          const stored = localStorage.getItem('kheldrop_auth_user');
          if (stored) {
            try {
              const u = JSON.parse(stored);
              if (u && (u.id || u.email)) {
                setUser(u);
                setDisplayName(u.user_metadata?.full_name || u.name || u.email?.split('@')[0] || '');
                setEmail(u.email || '');
              } else {
                setUser(null);
              }
            } catch {
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      } catch {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    }

    loadAuthUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        setDisplayName(session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '');
        setEmail(session.user.email || '');
      } else {
        setUser(null);
        setDisplayName('');
        setEmail('');
      }
    });

    if (typeof window !== 'undefined') {
      const storedPerm = localStorage.getItem('kheldrop_location_permission');
      if (storedPerm) setLocationPermission(storedPerm);

      const storedCoords = localStorage.getItem('kheldrop_live_coords');
      if (storedCoords) {
        try {
          setLocationCoords(JSON.parse(storedCoords));
        } catch {}
      }

      const storedPrefs = localStorage.getItem('kheldrop_player_preferences');
      if (storedPrefs) {
        try {
          const p = JSON.parse(storedPrefs);
          if (p.radiusKm) setRadiusKm(p.radiusKm);
        } catch {}
      }

      const storedCookies = localStorage.getItem('kheldrop_cookie_settings');
      if (storedCookies) {
        try {
          setCookieSettings(JSON.parse(storedCookies));
        } catch {}
      }
    }

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleToggleNotifications = async () => {
    const res = await requestNotificationPermission();
    setNotifPermission(res);
    if (res === 'granted') {
      sendBrowserNotification('KhelDrop Notifications Active', {
        body: 'Real-time match alerts and notifications are working properly!',
      });
    }
  };

  const handleRequestLiveLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported by this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocationCoords(coords);
        setLocationPermission('granted');
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_live_coords', JSON.stringify(coords));
          localStorage.setItem('kheldrop_location_permission', 'granted');
        }
      },
      () => {
        setLocationPermission('denied');
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_location_permission', 'denied');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof window !== 'undefined' && user) {
      const updated = {
        ...user,
        name: displayName,
        user_metadata: { ...(user.user_metadata || {}), full_name: displayName },
      };
      localStorage.setItem('kheldrop_auth_user', JSON.stringify(updated));
      setUser(updated);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2500);
    }
  };

  const handleCookieToggle = (key: keyof typeof cookieSettings) => {
    if (key === 'essential') return;
    const updated = { ...cookieSettings, [key]: !cookieSettings[key] };
    setCookieSettings(updated);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_cookie_settings', JSON.stringify(updated));
      localStorage.setItem('kheldrop_cookie_consent', 'custom');
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    if (typeof window !== 'undefined') {
      localStorage.removeItem('kheldrop_token');
      localStorage.removeItem('kheldrop_auth_user');
      sessionStorage.clear();
    }
    setUser(null);
    setDisplayName('');
    setEmail('');
    navigate('/');
  };

  const reliabilityScore = user?.checkInRate !== undefined ? Math.round(user.checkInRate * 100) : 100;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #fbfbfd)', color: 'var(--text, #0a0a0b)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--border, #e8e8ed)',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary, #6b6b76)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '6px 10px',
              borderRadius: '8px',
            }}
          >
            <ArrowLeft size={18} />
            Back to Map
          </button>
          <span style={{ color: 'var(--border, #e8e8ed)' }}>|</span>
          <div style={{ fontWeight: 800, fontSize: '15px' }}>Account & Privacy Settings</div>
        </div>

        <Link
          to="/help"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--elevated, #f4f4f6)',
            color: 'var(--text, #0a0a0b)',
            padding: '8px 16px',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
            border: '1px solid var(--border, #e8e8ed)',
          }}
        >
          <HelpCircle size={15} />
          Help Center
        </Link>
      </header>

      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '36px 20px 80px' }}>
        {!authLoading && !user ? (
          <div
            style={{
              background: 'var(--surface, #ffffff)',
              border: '1px solid var(--border, #e8e8ed)',
              borderRadius: '24px',
              padding: '36px 28px',
              marginBottom: '24px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'var(--elevated, #f4f4f6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: 'var(--text-secondary, #6b6b76)',
              }}
            >
              <User size={26} />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 6px', color: 'var(--text, #0a0a0b)' }}>
              Sign In Required
            </h2>
            <p
              style={{
                fontSize: '13.5px',
                color: 'var(--text-secondary, #6b6b76)',
                margin: '0 auto 20px',
                maxWidth: '420px',
                lineHeight: 1.5,
              }}
            >
              Sign in with your Supabase account to access your player profile, reliability score, and match history.
            </p>
            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#111111',
                color: '#ffffff',
                padding: '10px 24px',
                borderRadius: '999px',
                fontSize: '13.5px',
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              Sign In / Register
            </Link>
          </div>
        ) : user ? (
          <>
            <div
              style={{
                background: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e8e8ed)',
                borderRadius: '24px',
                padding: '28px',
                marginBottom: '24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#111111',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    fontWeight: 800,
                  }}
                >
                  {(displayName || email || 'P').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text, #0a0a0b)' }}>
                    {displayName || 'Verified Player'}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', marginTop: '2px' }}>
                    {email}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                    <span
                      style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '6px',
                        border: '1px solid #a7f3d0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Check size={11} /> Email Verified
                    </span>
                    <span
                      style={{
                        background: '#f5f3ff',
                        color: '#5b21b6',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '6px',
                        border: '1px solid #ddd6fe',
                      }}
                    >
                      {reliabilityScore}% Reliability Score
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#fff',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '8px 16px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <LogOut size={14} /> Log Out
                </button>
              </div>
            </div>

            <div
              style={{
                background: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e8e8ed)',
                borderRadius: '20px',
                padding: '26px',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <User size={18} color="#111111" />
                <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Player Profile</h2>
              </div>

              <form onSubmit={handleSaveProfile}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1px solid var(--border, #e8e8ed)',
                      background: 'var(--elevated, #f4f4f6)',
                      fontSize: '14px',
                      color: 'var(--text, #0a0a0b)',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                    Account Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1px solid var(--border, #e8e8ed)',
                      background: '#fafafb',
                      fontSize: '14px',
                      color: 'var(--text-muted, #9b9ba6)',
                      cursor: 'not-allowed',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      background: '#111111',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '999px',
                      padding: '10px 22px',
                      fontSize: '13.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Save Profile
                  </button>
                  {isSaved && (
                    <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Check size={14} /> Profile updated
                    </span>
                  )}
                </div>
              </form>
            </div>
          </>
        ) : null}

        {/* Browser Notifications Settings Card */}
        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Bell size={18} color="#111111" />
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Browser Notifications & Live Alerts</h2>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 0 18px', lineHeight: 1.5 }}>
            Get instant desktop and mobile browser alerts when other players join your sports lobbies, accept slot invites, or when games are confirmed.
          </p>

          <div
            style={{
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid var(--border, #e8e8ed)',
              background: '#fafafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>Notification Permission</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)', marginTop: '2px' }}>
                Status:{' '}
                <strong style={{ color: notifPermission === 'granted' ? '#16a34a' : notifPermission === 'denied' ? '#ef4444' : '#ea580c' }}>
                  {notifPermission.toUpperCase()}
                </strong>
              </div>
            </div>

            <button
              onClick={handleToggleNotifications}
              style={{
                background: '#111111',
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                padding: '8px 18px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Bell size={13} />
              {notifPermission === 'granted' ? 'Send Test Alert' : 'Enable Notifications'}
            </button>
          </div>
        </div>

        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <MapPin size={18} color="#111111" />
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Live Location & Match Radar</h2>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 0 18px', lineHeight: 1.5 }}>
            KhelDrop uses your GPS coordinates on-demand to calculate turf proximity. We do not track you continuously or sell telemetry.
          </p>

          <div
            style={{
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid var(--border, #e8e8ed)',
              background: '#fafafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>Live Location Permission</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)', marginTop: '2px' }}>
                Status:{' '}
                <strong style={{ color: locationPermission === 'granted' ? '#16a34a' : '#ea580c' }}>
                  {locationPermission.toUpperCase()}
                </strong>{' '}
                {locationCoords && '• Live GPS Active (Privacy Shield)'}
              </div>
            </div>

            <button
              onClick={handleRequestLiveLocation}
              style={{
                background: '#111111',
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                padding: '8px 16px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Navigation size={13} />
              {locationPermission === 'granted' ? 'Update GPS' : 'Allow Live Location'}
            </button>
          </div>

          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>
              Preferred Radar Discovery Radius
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[2, 5, 10, 25, 50].map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setRadiusKm(r);
                    if (typeof window !== 'undefined') {
                      const prev = JSON.parse(localStorage.getItem('kheldrop_player_preferences') || '{}');
                      localStorage.setItem('kheldrop_player_preferences', JSON.stringify({ ...prev, radiusKm: r }));
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '10px',
                    border: radiusKm === r ? '2px solid #111111' : '1px solid var(--border, #e8e8ed)',
                    background: radiusKm === r ? '#111111' : 'var(--elevated, #f4f4f6)',
                    color: radiusKm === r ? '#ffffff' : 'var(--text, #0a0a0b)',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {r} km
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Cookie size={18} color="#111111" />
              <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Cookie & Storage Preferences</h2>
            </div>
            <Link
              to="/cookie-preferences"
              style={{
                fontSize: '12.5px',
                fontWeight: 700,
                color: '#6d28d9',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Full Customizer <ExternalLink size={12} />
            </Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '12px', background: '#fafafb', border: '1px solid var(--border, #e8e8ed)' }}>
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 700 }}>Strictly Essential Cookies</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)' }}>Session authentication & security tokens</div>
              </div>
              <input type="checkbox" checked disabled style={{ width: '18px', height: '18px', accentColor: '#111111' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '12px', background: 'var(--surface, #ffffff)', border: '1px solid var(--border, #e8e8ed)' }}>
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 700 }}>Sport & Match Preferences</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)' }}>Remember active sport filters and map state</div>
              </div>
              <input
                type="checkbox"
                checked={cookieSettings.functional}
                onChange={() => handleCookieToggle('functional')}
                style={{ width: '18px', height: '18px', accentColor: '#111111', cursor: 'pointer' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: '12px', background: 'var(--surface, #ffffff)', border: '1px solid var(--border, #e8e8ed)' }}>
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 700 }}>Radar Telemetry & Cache</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)' }}>Instant turf loading on revisit</div>
              </div>
              <input
                type="checkbox"
                checked={cookieSettings.matchmaking}
                onChange={() => handleCookieToggle('matchmaking')}
                style={{ width: '18px', height: '18px', accentColor: '#111111', cursor: 'pointer' }}
              />
            </div>
          </div>
        </div>

        {/* Local Cache & Offline Storage Card */}
        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Zap size={18} color="#111111" />
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Local Cache & Offline Storage</h2>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 0 18px', lineHeight: 1.5 }}>
            KhelDrop caches game assets, playground map tiles, and sport categories locally to accelerate repeat load times and allow offline viewing of active pickup matches.
          </p>

          <div
            style={{
              padding: '16px',
              borderRadius: '14px',
              border: '1px solid var(--border, #e8e8ed)',
              background: '#fafafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>Local Storage Cache</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)', marginTop: '2px' }}>
                Status: <strong style={{ color: '#16a34a' }}>ACTIVE & READY</strong>
              </div>
            </div>

            <button
              onClick={() => {
                if (typeof window !== 'undefined' && 'caches' in window) {
                  caches.delete('kheldrop-cache-v1').then(() => {
                    caches.open('kheldrop-cache-v1');
                  });
                }
                setCacheMessage('Local cache refreshed successfully!');
                setTimeout(() => setCacheMessage(''), 3000);
              }}
              style={{
                background: '#111111',
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                padding: '8px 18px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <RefreshCw size={13} />
              Re-sync Cache
            </button>
          </div>

          {cacheMessage && (
            <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#16a34a', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Check size={14} /> {cacheMessage}
            </div>
          )}
        </div>


        {/* =========================================================================================
            PHASE 4: STATUTORY PRIVACY & DATA RIGHTS CENTER (DPDP ACT, 2023)
            ========================================================================================= */}
        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ShieldCheck size={20} color="#16a34a" />
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0, color: 'var(--text, #0a0a0b)' }}>
                  Privacy & Data Rights Center
                </h2>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)' }}>
                  DPDP Act, 2023 Statutory Compliance & Personal Data Governance
                </div>
              </div>
            </div>
            <Link
              to="/privacy"
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#6d28d9',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Privacy Notice <ExternalLink size={11} />
            </Link>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 0 20px', lineHeight: 1.5 }}>
            Under the Digital Personal Data Protection Act, 2023, you have direct, unhindered control over your digital footprint. Exercise your statutory rights below:
          </p>

          {/* User Rights Action Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {/* View My Data */}
            <button
              onClick={() => setViewDataModal(true)}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--border, #e8e8ed)',
                background: '#fafafb',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Eye size={18} color="#6d28d9" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text, #0a0a0b)' }}>View My Data</div>
                <div style={{ fontSize: '11px', color: '#6b6b76' }}>Inspect personal attributes & records</div>
              </div>
            </button>

            {/* Download My Data */}
            <button
              onClick={handleExportData}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--border, #e8e8ed)',
                background: '#fafafb',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Download size={18} color="#16a34a" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text, #0a0a0b)' }}>Download Data Export</div>
                <div style={{ fontSize: '11px', color: '#6b6b76' }}>Download full machine-readable JSON</div>
              </div>
            </button>

            {/* Raise Grievance */}
            <button
              onClick={() => setGrievanceModal(true)}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--border, #e8e8ed)',
                background: '#fafafb',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Scale size={18} color="#f59e0b" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text, #0a0a0b)' }}>File Grievance / Complaint</div>
                <div style={{ fontSize: '11px', color: '#6b6b76' }}>Direct appeal to DPO Ashutosh Yadav</div>
              </div>
            </button>

            {/* Delete Account */}
            <button
              onClick={() => setDeleteAccountModal(true)}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                background: 'rgba(239, 68, 68, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Trash2 size={18} color="#ef4444" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#ef4444' }}>Delete Account</div>
                <div style={{ fontSize: '11px', color: '#6b6b76' }}>Section 12 permanent erasure</div>
              </div>
            </button>
          </div>

          {/* Consent Ledger & Toggles */}
          <div style={{ borderTop: '1px solid var(--border, #e8e8ed)', paddingTop: '18px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text, #0a0a0b)', marginBottom: '12px' }}>
              Affirmative Consent Controls (Withdrawal Management)
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Account Service */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '10px', background: '#fafafb', border: '1px solid var(--border, #e8e8ed)' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>Core Service Processing</div>
                  <div style={{ fontSize: '11.5px', color: '#6b6b76' }}>Account authentication, match rosters, and communication.</div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: '2px 8px', borderRadius: '999px' }}>
                  Mandatory for Service
                </span>
              </div>

              {/* Marketing Consent */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '10px', background: '#ffffff', border: '1px solid var(--border, #e8e8ed)' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>Promotional Updates & Emails</div>
                  <div style={{ fontSize: '11.5px', color: '#6b6b76' }}>Community tournament news and product announcements.</div>
                </div>
                <input
                  type="checkbox"
                  checked={consentState.marketing}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setConsentState((prev) => ({ ...prev, marketing: checked }));
                    fetch(checked ? '/api/privacy/consent' : '/api/privacy/consent/withdraw', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ userId: user?.id, purpose: 'MARKETING_COMMUNICATIONS' }),
                    }).catch(() => {});
                  }}
                  style={{ width: '18px', height: '18px', accentColor: '#6d28d9', cursor: 'pointer' }}
                />
              </div>

              {/* Location Checkin */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '10px', background: '#ffffff', border: '1px solid var(--border, #e8e8ed)' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>Single-Event GPS Check-In</div>
                  <div style={{ fontSize: '11.5px', color: '#6b6b76' }}>One-time venue radius check when you tap "Check In".</div>
                </div>
                <input
                  type="checkbox"
                  checked={consentState.locationCheckin}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setConsentState((prev) => ({ ...prev, locationCheckin: checked }));
                    fetch(checked ? '/api/privacy/consent' : '/api/privacy/consent/withdraw', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ userId: user?.id, purpose: 'LOCATION_CHECKIN' }),
                    }).catch(() => {});
                  }}
                  style={{ width: '18px', height: '18px', accentColor: '#6d28d9', cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* VIEW DATA MODAL */}
        {viewDataModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '520px', width: '100%', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', color: '#111' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800 }}>My Personal Data Profile</h3>
                <button onClick={() => setViewDataModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', color: '#444' }}><X size={18} /></button>
              </div>
              <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto' }}>
                <div style={{ padding: '8px 12px', background: '#f4f4f6', borderRadius: '8px' }}>
                  <strong>User ID:</strong> {user?.id || 'guest_user'}
                </div>
                <div style={{ padding: '8px 12px', background: '#f4f4f6', borderRadius: '8px' }}>
                  <strong>Display Name:</strong> {displayName || 'Not Set'}
                </div>
                <div style={{ padding: '8px 12px', background: '#f4f4f6', borderRadius: '8px' }}>
                  <strong>Email:</strong> {email || 'Not Provided'}
                </div>
                <div style={{ padding: '8px 12px', background: '#f4f4f6', borderRadius: '8px' }}>
                  <strong>Reliability Score:</strong> 100% (Default Safe Tier)
                </div>
                <div style={{ padding: '8px 12px', background: '#f4f4f6', borderRadius: '8px' }}>
                  <strong>GPS Telemetry Retention:</strong> None (Zero Background Tracking Active)
                </div>
              </div>
              <button
                onClick={() => setViewDataModal(false)}
                style={{ marginTop: '18px', width: '100%', padding: '10px', background: '#111', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
              >
                Close Record
              </button>
            </div>
          </div>
        )}

        {/* GRIEVANCE FILING MODAL */}
        {grievanceModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '500px', width: '100%', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', color: '#111' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800 }}>Submit Statutory Grievance</h3>
                <button onClick={() => { setGrievanceModal(false); setGrievanceTicket(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', color: '#444' }}><X size={18} /></button>
              </div>

              {grievanceTicket ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <CheckCircle size={44} color="#16a34a" style={{ margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 800 }}>Complaint Registered</h4>
                  <div style={{ padding: '8px 14px', background: '#f4f4f6', borderRadius: '8px', fontFamily: 'monospace', fontSize: '15px', color: '#6d28d9', fontWeight: 700, margin: '8px 0' }}>
                    {grievanceTicket}
                  </div>
                  <p style={{ fontSize: '12.5px', color: '#6b6b76', margin: '8px 0 16px' }}>
                    Your complaint has been assigned to Data Protection Officer <strong>Ashutosh Yadav</strong>. Acknowledgment sent within 24 hours; resolution within 7 days.
                  </p>
                  <button
                    onClick={() => { setGrievanceModal(false); setGrievanceTicket(null); }}
                    style={{ padding: '8px 20px', background: '#111', color: '#fff', border: 'none', borderRadius: '999px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleGrievanceSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Grievance Category</label>
                    <select
                      value={grievanceCategory}
                      onChange={(e) => setGrievanceCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d4d4d8', fontSize: '13px' }}
                    >
                      <option value="privacy">Privacy & Personal Data Processing (DPDP Act)</option>
                      <option value="harassment">Player Misconduct or Harassment</option>
                      <option value="safety">Venue Safety Hazard or Turf Defect</option>
                      <option value="unlawful_content">Unlawful or Misleading Content</option>
                      <option value="copyright">Copyright / IP Infringement</option>
                      <option value="other">Other Platform Grievance</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Complaint Description</label>
                    <textarea
                      required
                      rows={4}
                      value={grievanceDesc}
                      onChange={(e) => setGrievanceDesc(e.target.value)}
                      placeholder="Detail the circumstances, match ID, or user involved..."
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d4d4d8', fontSize: '13px', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ fontSize: '11px', color: '#71717a', lineHeight: 1.4 }}>
                    Statutory Timelines: Receipt acknowledgment generated immediately; investigation concluded within 7 calendar days. Escalation: DPBI (https://dpbi.gov.in).
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingGrievance}
                    style={{
                      padding: '10px',
                      background: '#6d28d9',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {isSubmittingGrievance ? 'Submitting...' : 'File Official Grievance'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* DELETE ACCOUNT MODAL */}
        {deleteAccountModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '460px', width: '100%', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', color: '#111' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ef4444', marginBottom: '12px' }}>
                <AlertOctagon size={24} />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800 }}>Permanent Account Erasure</h3>
              </div>
              <p style={{ fontSize: '13px', color: '#6b6b76', margin: '0 0 16px', lineHeight: 1.5 }}>
                Under Section 12 of the DPDP Act, 2023, account deletion permanently revokes credentials, de-identifies player attendance records, and removes personal contact data. This action is irreversible.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
                  Type <span style={{ color: '#ef4444' }}>DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d4d4d8', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setDeleteAccountModal(false)}
                  style={{ flex: 1, padding: '10px', background: '#f4f4f6', color: '#111', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  disabled={deleteConfirmation !== 'DELETE' || isDeleting}
                  onClick={handleExecuteDeleteAccount}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: deleteConfirmation === 'DELETE' ? '#ef4444' : '#fca5a5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    cursor: deleteConfirmation === 'DELETE' ? 'pointer' : 'not-allowed',
                  }}
                >
                  {isDeleting ? 'Erasing...' : 'Confirm Erasure'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8e8ed)',
            borderRadius: '20px',
            padding: '26px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Shield size={18} color="#111111" />
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Trust, Legal & Support Hub</h2>
          </div>

          <div
            style={{
              padding: '16px',
              borderRadius: '14px',
              background: 'rgba(109, 40, 217, 0.05)',
              border: '1px solid rgba(109, 40, 217, 0.15)',
              marginBottom: '18px',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#6d28d9', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Mail size={16} /> Official Help Center Contact
            </div>
            <p style={{ fontSize: '12.5px', color: 'var(--text, #0a0a0b)', margin: '4px 0 6px' }}>
              Questions about match hosting, account security, or responsible disclosure? Email us directly:
            </p>
            <a
              href="mailto:ashutosh4tech@gmail.com"
              style={{
                color: '#6d28d9',
                fontWeight: 700,
                fontSize: '14px',
                textDecoration: 'underline',
              }}
            >
              ashutosh4tech@gmail.com
            </a>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
            <Link to="/privacy" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Privacy Policy
            </Link>
            <Link to="/terms" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Terms of Service
            </Link>
            <Link to="/cookies" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Cookie Policy
            </Link>
            <Link to="/accessibility" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Accessibility Statement
            </Link>
            <Link to="/dpa" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Data Processing (DPA)
            </Link>
            <Link to="/security" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Security Policy
            </Link>
            <Link to="/disclosure" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Responsible Disclosure
            </Link>
            <Link to="/community" style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--elevated)', color: 'var(--text)', textDecoration: 'none', fontSize: '12.5px', fontWeight: 600 }}>
              Community Guidelines
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
